import React from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import logger from '../utils/logger';

/**
 * Reusable Change Log Modal component
 * Extracted from ScheduleDisplay to enable reuse across components
 */
const ChangeLogModal = ({
    isOpen,
    onClose,
    changeLog = [],
    onSave,
    isSaving = false,
    saveButtonText = "Save Changes",
    title = null
}) => {
    const modalTitle = title || `Review Changes (${changeLog.length} net modification${changeLog.length !== 1 ? 's' : ''})`;

    // Log modal state changes
    React.useEffect(() => {
        if (isOpen) {
            logger.debug('ChangeLogModal: Modal opened', {
                changeCount: changeLog.length,
                hasChanges: changeLog.length > 0,
                modalTitle
            });
        }
    }, [isOpen, changeLog.length, modalTitle]);

    const handleSave = async () => {
        logger.debug('ChangeLogModal: handleSave called', {
            hasOnSave: !!onSave,
            onSaveType: typeof onSave,
            changeLogLength: changeLog.length
        });

        if (onSave && typeof onSave === 'function') {
            try {
                logger.debug('ChangeLogModal: Starting save operation');
                const result = await onSave();
                logger.info('ChangeLogModal: Save completed successfully', result);
            } catch (error) {
                logger.error('ChangeLogModal: Save failed', error);
                logger.error('ChangeLogModal: Save error details', {
                    message: error.message,
                    stack: error.stack,
                    name: error.name
                });
                // Error handling is left to the parent component
            }
        } else {
            logger.warn('ChangeLogModal: No valid onSave function provided');
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
            {changeLog.length > 0 ? (
                <ul className="space-y-3 max-h-[60vh] overflow-y-auto text-sm">
                    {changeLog.map((log, index) => (
                        <li key={`${log.itemId}-${log.action}-${index}`} className="border-b pb-2">
                            <p className="font-semibold text-gray-700">({log.action.toUpperCase()})</p>
                            <p className="text-gray-600 ml-2">{log.details}</p>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-center text-gray-500">No net changes detected compared to the initial state.</p>
            )}
            <div className="mt-6 flex justify-end space-x-3">
                <Button variant="secondary" onClick={onClose}>Close</Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={changeLog.length === 0 || isSaving}
                    className="relative"
                >
                    {isSaving && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        </div>
                    )}
                    <span className={isSaving ? 'opacity-0' : 'opacity-100 flex items-center justify-center'}>
                        {isSaving ? 'Saving...' : saveButtonText}
                    </span>
                </Button>
            </div>
        </Modal>
    );
};

export default ChangeLogModal; 